import { formatCurrency, type Locale } from "@saas/internationalization";
import { Check, ChevronRight, Globe2, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { copy } from "./content";

export function LandingPage({ locale }: { locale: Locale }) {
  const text = copy[locale];
  const [dark, setDark] = useState(false);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = text.meta.title;
    const storedTheme = localStorage.getItem("theme");
    setDark(
      storedTheme === "dark" ||
        (!storedTheme && matchMedia("(prefers-color-scheme: dark)").matches),
    );
  }, [locale, text.meta.title]);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  const sectionLinks = ["benefits", "features", "use-cases", "pricing", "faq"];
  return (
    <div className="min-h-screen bg-white text-zinc-950 antialiased dark:bg-zinc-950 dark:text-zinc-50">
      <a href="#main" className="skip-link">
        {text.hero[3]}
      </a>
      <header className="sticky top-0 z-40 border-b border-zinc-200/70 bg-white/85 backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/80">
        <nav
          className="shell flex h-16 items-center justify-between"
          aria-label={text.nav[0]}
        >
          <Link
            to={`/${locale}`}
            className="flex items-center gap-2 font-semibold"
          >
            <span className="logo-mark">S</span>
            <span>TrackBoss</span>
          </Link>
          <div className="hidden items-center gap-7 text-sm text-zinc-600 dark:text-zinc-300 lg:flex">
            {sectionLinks.map((id, i) => (
              <a
                key={id}
                href={`#${id}`}
                className="hover:text-zinc-950 dark:hover:text-white"
              >
                {text.nav[i]}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="group relative">
              <button
                type="button"
                className="icon-button"
                aria-label="Language"
              >
                <Globe2 size={18} />
              </button>
              <div className="locale-menu">
                {(["en", "pt-BR", "pt-PT", "es"] as const).map((item) => (
                  <Link
                    key={item}
                    to={`/${item}`}
                    lang={item}
                    className={item === locale ? "active" : ""}
                  >
                    {item}
                  </Link>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={() =>
                setDark((value) => {
                  localStorage.setItem("theme", value ? "light" : "dark");
                  return !value;
                })
              }
              aria-label="Theme"
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link
              to={`/${locale}/login`}
              className="hidden text-sm font-medium sm:block"
            >
              {text.nav[5]}
            </Link>
            <Link to={`/${locale}/register`} className="button button-dark">
              {text.nav[6]}
            </Link>
          </div>
        </nav>
      </header>
      <main id="main">
        <section className="hero-grid overflow-hidden border-b border-zinc-200 dark:border-zinc-800">
          <div className="shell relative py-24 sm:py-32 lg:py-40">
            <div className="hero-orb" aria-hidden="true" />
            <div className="relative max-w-4xl">
              <p className="eyebrow">{text.hero[0]}</p>
              <h1>{text.hero[1]}</h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-zinc-600 dark:text-zinc-300 sm:text-xl">
                {text.hero[2]}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link className="button button-dark" to={`/${locale}/register`}>
                  {text.hero[3]} <ChevronRight size={17} />
                </Link>
                <a className="button button-light" href="#features">
                  {text.hero[4]}
                </a>
              </div>
              <p className="mt-6 text-sm text-zinc-500">{text.hero[5]}</p>
            </div>
          </div>
        </section>
        <TextCards id="benefits" content={text.benefits} columns={3} />
        <TextCards id="features" content={text.features} columns={4} muted />
        <TextCards id="use-cases" content={text.useCases} columns={3} />
        <section
          id="pricing"
          className="section border-y border-zinc-200 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/40"
        >
          <div className="shell">
            <p className="eyebrow">{text.pricing[0]}</p>
            <h2>{text.pricing[1]}</h2>
            <div className="mt-4 flex gap-2 text-sm">
              <span className="pill">{text.pricing[2]}</span>
              <span className="pill">{text.pricing[3]}</span>
            </div>
            <div className="mt-12 grid gap-5 lg:grid-cols-3">
              {text.plans.map((plan, i) => (
                <article
                  key={plan.name}
                  className={`price-card ${i === 1 ? "featured" : ""}`}
                >
                  {i === 1 && (
                    <span className="popular">{text.pricing[5]}</span>
                  )}
                  <h3>{plan.name}</h3>
                  <p className="mt-3 text-zinc-500 dark:text-zinc-400">
                    {plan.description}
                  </p>
                  <p className="mt-7 text-5xl font-semibold tracking-tight">
                    {formatCurrency(
                      plan.monthlyPriceMinor / 100,
                      "BRL",
                      locale,
                    )}
                    <span className="text-base font-normal text-zinc-500">
                      {` ${text.pricing[6]}`}
                    </span>
                  </p>
                  <ul className="mt-7 space-y-3">
                    {plan.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Check size={17} className="text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link
                    className={`button mt-8 w-full ${i === 1 ? "button-dark" : "button-light"}`}
                    to={`/${locale}/register`}
                  >
                    {text.pricing[4]} {plan.name}
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
        <section id="faq" className="section">
          <div className="shell grid gap-12 lg:grid-cols-[.8fr_1.2fr]">
            <div>
              <p className="eyebrow">FAQ</p>
              <h2>{text.nav[4]}</h2>
            </div>
            <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {text.faq.map((item) => (
                <details key={item.q} className="group py-5">
                  <summary className="cursor-pointer list-none pr-8 text-lg font-medium">
                    {item.q}
                  </summary>
                  <p className="mt-3 max-w-2xl leading-7 text-zinc-600 dark:text-zinc-300">
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
        <section className="shell pb-24">
          <div className="cta">
            <h2>{text.cta[0]}</h2>
            <p>{text.cta[1]}</p>
            <Link
              className="button bg-white text-zinc-950"
              to={`/${locale}/register`}
            >
              {text.cta[2]} <ChevronRight size={17} />
            </Link>
          </div>
        </section>
      </main>
      <footer className="border-t border-zinc-200 py-10 text-sm text-zinc-500 dark:border-zinc-800">
        <div className="shell flex flex-col justify-between gap-5 sm:flex-row">
          <span>TrackBoss</span>
          <span>{text.footer}</span>
        </div>
      </footer>
    </div>
  );
}

function TextCards({
  id,
  content,
  columns,
  muted = false,
}: {
  id: string;
  content: string[];
  columns: number;
  muted?: boolean;
}) {
  const gridColumns = columns === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";
  const items = Array.from(
    { length: (content.length - 2) / 2 },
    (_, index) => ({
      title: content[2 + index * 2] ?? "",
      description: content[3 + index * 2] ?? "",
    }),
  );
  return (
    <section
      id={id}
      className={`section ${muted ? "bg-zinc-950 text-white dark:bg-black" : ""}`}
    >
      <div className="shell">
        <p className="eyebrow">{content[0]}</p>
        <h2 className="max-w-3xl">{content[1]}</h2>
        <div
          className={`mt-12 grid gap-px overflow-hidden rounded-2xl border ${gridColumns} ${muted ? "border-zinc-800 bg-zinc-800" : "border-zinc-200 bg-zinc-200 dark:border-zinc-800 dark:bg-zinc-800"}`}
        >
          {items.map((item, index) => (
            <article
              key={item.title}
              className={`min-h-56 p-7 ${muted ? "bg-zinc-950 dark:bg-black" : "bg-white dark:bg-zinc-950"}`}
            >
              <span className="text-xs text-zinc-500">0{index + 1}</span>
              <h3 className="mt-12 text-xl font-semibold">{item.title}</h3>
              <p className="mt-3 leading-7 text-zinc-500 dark:text-zinc-400">
                {item.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
